import * as React from 'react';

import {
    formatNumber,
    parseNumber
} from '../../service/FormatHelpers/FormatHelpers';
import { Omit } from '../../typings/utils';
import { DirectFormInput } from '../DirectFormInput/DirectFormInput';
import { InputFieldTextProps, InputFieldType } from '../InputField/InputField';

const DEFAULT_FRACTIONAL_PART_LENGTH = 2;

export interface NumberInputProps
    extends Omit<InputFieldTextProps, 'text' | 'inputType'> {
    value?: number;
    onValueChange: (value: number | undefined) => void;
    fractionalPartLength?: number;
    valid?: boolean;
}

export interface NumberInputState {
    inputValue: string;
    isFocused: boolean;
}

export class NumberInput extends React.PureComponent<
    NumberInputProps,
    NumberInputState
> {
    static getStringFromValue(
        value?: number,
        factorialPartLength: number = DEFAULT_FRACTIONAL_PART_LENGTH
    ) {
        return value !== undefined
            ? formatNumber(value, factorialPartLength)
            : '';
    }

    componentDidUpdate(prevProps: NumberInputProps) {
        const newValueString = NumberInput.getStringFromValue(
            this.props.value,
            this.props.fractionalPartLength
        );

        const oldValueString = NumberInput.getStringFromValue(
            prevProps.value,
            prevProps.fractionalPartLength
        );

        if (
            newValueString !== oldValueString ||
            (newValueString !== this.state.inputValue && !this.state.isFocused)
        ) {
            this.setState({
                inputValue: newValueString
            });
        }
    }

    constructor(props: NumberInputProps) {
        super(props);

        this.state = {
            inputValue: NumberInput.getStringFromValue(
                props.value,
                props.fractionalPartLength
            ),
            isFocused: false
        };
    }

    render() {
        return (
            <DirectFormInput
                {...this.props}
                text={this.state.inputValue}
                inputType={InputFieldType.TEXT}
                onChange={this.onChange}
                onFocus={this.onFocus}
                onBlur={this.onBlur}
            />
        );
    }

    private onChange = (value: string) => {
        const { onChange } = this.props;

        this.setState({
            inputValue: value
        });

        if (onChange) {
            onChange(value);
        }
    };

    private onBlur = (event: React.FocusEvent<HTMLElement>) => {
        const { onBlur, onValueChange, fractionalPartLength } = this.props;
        const { inputValue } = this.state;

        const parsedValue = parseNumber(inputValue);

        const numberValue =
            isNaN(parsedValue) || inputValue.trim() === ''
                ? undefined
                : parsedValue;

        this.setState({
            inputValue: NumberInput.getStringFromValue(
                numberValue,
                fractionalPartLength
            ),
            isFocused: false
        });

        onValueChange(numberValue);

        if (onBlur) {
            onBlur(event);
        }
    };

    private onFocus = () => {
        this.setState({
            isFocused: true
        });
    };
}
